import { ROOM_ENDPOINTS } from './config';
import { apiFetch } from './client';
import { formatApiValidationDetail } from './apiErrors';

const DEFAULT_ROOM = {
  name: 'My Project',
  description: "Lets create an awesome interior!",
};

/**
 * GET /api/v1/rooms
 * Lists existing rooms (projects).
 */
export async function listRooms() {
  const res = await apiFetch(ROOM_ENDPOINTS.create, {
    method: 'GET',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || `Failed to list rooms (${res.status})`;
    throw new Error(msg);
  }

  console.log('GET /rooms response:', result);
  return result;
}

/**
 * POST /api/v1/rooms
 * Creates a new room. Returns { id, ... }
 */
export async function createRoom(data = DEFAULT_ROOM) {
  const res = await apiFetch(ROOM_ENDPOINTS.create, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || `Failed to create room (${res.status})`;
    throw new Error(msg);
  }

  // Debug: verify API response structure (POST /rooms returns id)
  //console.log('POST /rooms response:', result);
  if (result.id != null) {
    //console.log('id:', result.id);
  } else {
    console.warn('POST /rooms did not return id. Keys:', Object.keys(result));
  }

  return result;
}

/**
 * GET /api/v1/rooms/{room_id}/images
 * Lists images for a room. Returns array of { id, roomId, s3Key, operationType, prompt, createdAt }.
 */
export async function getRoomImages(roomId) {
  const res = await apiFetch(ROOM_ENDPOINTS.uploadImage(roomId), {
    method: 'GET',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || `Failed to list room images (${res.status})`;
    throw new Error(msg);
  }

  return Array.isArray(result) ? result : [];
}

/**
 * GET /api/v1/rooms/{room_id}
 * Returns room details including latestRoomImageId (the current base image for AI operations).
 */
export async function getRoom(roomId) {
  const res = await apiFetch(ROOM_ENDPOINTS.getRoom(roomId), {
    method: 'GET',
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = result.message || result.error || `Failed to get room (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return result;
}

/**
 * Room image API payloads (POST/PUT /rooms/{id}/images, GET list entries) may include width/height.
 * Use for layout/aspect fallback when the browser image has not decoded yet.
 * @param {Record<string, unknown>} obj
 * @returns {{ width: number, height: number } | null}
 */
export function parseRoomImageDimensionsFromApi(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const w = Number(obj.width);
  const h = Number(obj.height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { width: Math.round(w), height: Math.round(h) };
}

/**
 * POST /api/v1/rooms/{room_id}/images
 * Uploads image to the room. Returns API response.
 */
export async function uploadRoomImage(roomId, imageFile) {
  const formData = new FormData();
  // Try 'image' - common for /rooms/{id}/images; API may expect this field name
  formData.append('image', imageFile);

  const res = await apiFetch(ROOM_ENDPOINTS.uploadImage(roomId), {
    method: 'POST',
    body: formData,
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    // 422 returns validation details - log for debugging
    console.error('Upload 422 response:', result);
    const msg =
      result.detail ||
      result.message ||
      result.error ||
      (typeof result === 'object' && JSON.stringify(result)) ||
      `Failed to upload image (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  //console.log('POST /rooms/{room_id}/images response:', result);
  return result;
}

/**
 * POST /api/v1/rooms/{room_id}/images/{parent_room_image_id}
 * Uploads a derived image from a parent room image (e.g. curated model composite).
 * @param {number} roomId
 * @param {number} parentRoomImageId
 * @param {File|Blob} imageFile
 * @returns {Promise<any>}
 */
export async function uploadRoomChildImage(roomId, parentRoomImageId, imageFile) {
  const formData = new FormData();
  formData.append('image', imageFile);

  const res = await apiFetch(
    ROOM_ENDPOINTS.uploadChildImage(roomId, parentRoomImageId),
    {
      method: 'POST',
      body: formData,
    },
  );

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    console.error('Upload child image response:', result);
    const msg =
      result.detail ||
      result.message ||
      result.error ||
      (typeof result === 'object' && JSON.stringify(result)) ||
      `Failed to upload child image (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * POST /api/v1/rooms/{room_id}/objects
 * Creates a room object with name/category/image payload.
 * @param {number} roomId
 * @param {{ name: string, category: string, image: File, prompt?: string }} payload
 * @returns {Promise<any>}
 */
export async function createRoomObject(roomId, payload) {
  const formData = new FormData();
  formData.append('name', payload?.name ?? '');
  formData.append('category', payload?.category ?? '');
  formData.append('image', payload?.image);
  const prompt = typeof payload?.prompt === 'string' ? payload.prompt.trim() : '';
  if (prompt) {
    formData.append('prompt', prompt);
  }

  const res = await apiFetch(ROOM_ENDPOINTS.objects(roomId), {
    method: 'POST',
    body: formData,
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      result.detail ||
      result.message ||
      result.error ||
      (typeof result === 'object' && JSON.stringify(result)) ||
      `Failed to create room object (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * GET /api/v1/rooms/{room_id}/objects
 * Lists saved room objects for a room.
 * @param {number} roomId
 * @returns {Promise<Array<{ id: number, roomId: number, name: string, category: string, imageS3Key: string }>>}
 */
export async function getRoomObjects(roomId) {
  const res = await apiFetch(ROOM_ENDPOINTS.objects(roomId), {
    method: 'GET',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      result.detail ||
      result.message ||
      result.error ||
      (typeof result === 'object' && JSON.stringify(result)) ||
      `Failed to list room objects (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  const objects = Array.isArray(result?.roomObjects)
    ? result.roomObjects
    : Array.isArray(result?.objects)
      ? result.objects
      : [];
  return objects.map(normalizeRoomObjectFromApi);
}

function normalizeRoomObjectFromApi(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const nested = obj.image && typeof obj.image === 'object' ? obj.image : null;
  return {
    ...obj,
    id: obj.id ?? obj.object_id ?? obj.roomObjectId ?? obj.room_object_id,
    imageS3Key:
      obj.imageS3Key ??
      obj.image_s3_key ??
      nested?.imageS3Key ??
      nested?.image_s3_key ??
      nested?.s3Key ??
      '',
    aiResultImageS3Key:
      obj.aiResultImageS3Key ??
      obj.ai_result_image_s3_key ??
      nested?.aiResultImageS3Key ??
      nested?.ai_result_image_s3_key ??
      '',
    imageUrl: obj.imageUrl ?? obj.image_url ?? nested?.url ?? nested?.imageUrl ?? '',
  };
}

/**
 * DELETE /api/v1/rooms/{room_id}/objects/{object_id}
 * @param {number} roomId
 * @param {number|string} objectId
 */
export async function deleteRoomObject(roomId, objectId) {
  const base = ROOM_ENDPOINTS.objects(roomId).replace(/\/$/, '');
  const res = await apiFetch(`${base}/${objectId}`, {
    method: 'DELETE',
  });

  if (res.status === 204 || res.status === 200) {
    return;
  }

  const result = await res.json().catch(() => ({}));
  const msg =
    result.detail ||
    result.message ||
    result.error ||
    (typeof result === 'object' && JSON.stringify(result)) ||
    `Failed to delete room object (${res.status})`;
  throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
}

/**
 * PUT /api/v1/rooms/{room_id}/objects/{object_id}
 * Updates a room object's image (used here to swap image after background removal).
 * @param {number} roomId
 * @param {number|string} objectId
 * @param {{ ai_result_image_s3_key: string }} body
 * @returns {Promise<any>}
 */
export async function updateRoomObject(roomId, objectId, body) {
  const base = ROOM_ENDPOINTS.objects(roomId).replace(/\/$/, '');
  const res = await apiFetch(`${base}/${objectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      result.detail ||
      result.message ||
      result.error ||
      (typeof result === 'object' && JSON.stringify(result)) ||
      `Failed to update room object (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * PATCH /api/v1/rooms/{room_id}/objects/{object_id}
 * Updates room object metadata (name, category).
 * @param {number} roomId
 * @param {number|string} objectId
 * @param {{ name?: string, category?: string }} body
 * @returns {Promise<any>}
 */
export async function patchRoomObject(roomId, objectId, body) {
  const base = ROOM_ENDPOINTS.objects(roomId).replace(/\/$/, '');
  const res = await apiFetch(`${base}/${objectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      result.detail ||
      result.message ||
      result.error ||
      (typeof result === 'object' && JSON.stringify(result)) ||
      `Failed to update room object (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * PUT /api/v1/rooms/{room_id}/images
 * Saves the AI result image as a new room image (same body as apply-image; prompt empty).
 * @param {number} roomId
 * @param {{ parent_room_image_id: number, ai_result_image_s3_key: string, operation_type: string, prompt?: string }} body
 * @returns {Promise<any>}
 */
export async function applyImageToRoom(roomId, body) {
  const url = ROOM_ENDPOINTS.uploadImage(roomId);
  console.log('PUT /rooms/{room_id}/images request:', { url, body });
  const res = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = result.message || result.error || result.detail || `Failed to save AI result image (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  console.log('PUT /rooms/{room_id}/images response:', result);
  return result;
}

/**
 * POST /api/v1/rooms/{room_id}/images/{image_id}/render-history
 * Saves a room image render to render history.
 * @param {number} roomId
 * @param {number} imageId
 * @param {{
 *   renderType?: 'room_image' | 'flythrough',
 *   resultImageS3Key?: string,
 *   flythroughS3Key?: string,
 *   thumbnailS3Key?: string,
 *   config: object,
 * }} body
 * @returns {Promise<any>}
 */
export async function saveRenderHistory(roomId, imageId, body) {
  const url = ROOM_ENDPOINTS.renderHistory(roomId, imageId);
  console.log('POST /rooms/{room_id}/images/{image_id}/render-history request:', { url, body });
  const res = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detailMsg = formatApiValidationDetail(result.detail);
    const msg =
      detailMsg ||
      result.message ||
      result.error ||
      `Failed to save render history (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  console.log('POST /rooms/{room_id}/images/{image_id}/render-history response:', result);
  return result;
}

function normalizeRenderHistoryList(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.results)) return result.results;
  return [];
}

/**
 * GET /api/v1/rooms/{room_id}/images/{image_id}/render-history
 * @param {number} roomId
 * @param {number} imageId
 * @param {{ renderType?: 'room_image' | 'flythrough' | null }} [options]
 * @returns {Promise<Array>}
 */
export async function getRenderHistory(roomId, imageId, { renderType } = {}) {
  const params = new URLSearchParams();
  if (renderType != null && String(renderType).trim()) {
    params.set('renderType', String(renderType).trim());
  }
  const query = params.toString();
  const url = `${ROOM_ENDPOINTS.renderHistory(roomId, imageId)}${query ? `?${query}` : ''}`;
  const res = await apiFetch(url, { method: 'GET' });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detailMsg = formatApiValidationDetail(result.detail);
    const msg =
      detailMsg ||
      result.message ||
      result.error ||
      `Failed to load render history (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return normalizeRenderHistoryList(result);
}

/**
 * DELETE /api/v1/rooms/{room_id}/images/{image_id}/render-history/{render_history_id}
 * @param {number} roomId
 * @param {number} imageId
 * @param {number} renderHistoryId
 * @returns {Promise<void>}
 */
export async function deleteRenderHistory(roomId, imageId, renderHistoryId) {
  const url = ROOM_ENDPOINTS.renderHistoryEntry(roomId, imageId, renderHistoryId);
  const res = await apiFetch(url, { method: 'DELETE' });

  if (res.status === 204 || res.status === 200) {
    return;
  }

  const result = await res.json().catch(() => ({}));
  const detailMsg = formatApiValidationDetail(result.detail);
  const msg =
    detailMsg ||
    result.message ||
    result.error ||
    `Failed to delete render history (${res.status})`;
  throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
}

/**
 * GET /api/v1/rooms/{room_id}/images/{image_id}/interiors
 * Returns existing interior polygons for an image.
 * @param {number} roomId
 * @param {number} imageId - room_image_id
 * @returns {Promise<Array<{ interior_type_id?, confidence?, bbox?, polygon?, label?, ... }>>}
 */
export async function getInteriors(roomId, imageId) {
  const res = await apiFetch(ROOM_ENDPOINTS.interiors(roomId, imageId), {
    method: 'GET',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || `Failed to get interiors (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return Array.isArray(result) ? result : [];
}

/**
 * POST /api/v1/rooms/{room_id}/images/{image_id}/interiors
 * Saves interior layer data (polygons, bbox) for an image.
 * @param {number} roomId
 * @param {number} imageId - room_image_id
 * @param {Array<{ interior_type_id?: number, confidence?: number, bbox?: number[], polygon?: any }>} interiors
 * @returns {Promise<any>}
 */
export async function saveInteriors(roomId, imageId, interiors) {
  const res = await apiFetch(ROOM_ENDPOINTS.interiors(roomId, imageId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(interiors),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || result.detail || `Failed to save interiors (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * PATCH /api/v1/rooms/{room_id}/images/{image_id}/interiors
 * Updates existing interior layer data for an image (e.g. after manual edits).
 * API contract is expected to mirror POST shape.
 * @param {number} roomId
 * @param {number} imageId - room_image_id
 * @param {Array<{ interior_type_id?: number, confidence?: number, bbox?: number[], polygon?: any }>} interiors
 * @returns {Promise<any>}
 */
export async function updateInteriors(roomId, imageId, interiors) {
  const res = await apiFetch(ROOM_ENDPOINTS.interiors(roomId, imageId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(interiors),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      result.message || result.error || result.detail || `Failed to update interiors (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * DELETE /api/v1/rooms/{room_id}/images/{image_id}/interiors/{interior_id}
 * Deletes a single interior object.
 * @param {number} roomId
 * @param {number} imageId - room_image_id
 * @param {number} interiorId
 * @returns {Promise<any>}
 */
export async function deleteInterior(roomId, imageId, interiorId) {
  const res = await apiFetch(ROOM_ENDPOINTS.interiorDelete(roomId, imageId, interiorId), {
    method: 'DELETE',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || result.detail || `Failed to delete interior (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * POST /api/v1/rooms/{room_id}/images/{image_id}/interiors/{interior_id}/restore
 * Restores a previously deleted interior object.
 * @param {number} roomId
 * @param {number} imageId - room_image_id
 * @param {number} interiorId
 * @returns {Promise<any>}
 */
export async function restoreInterior(roomId, imageId, interiorId) {
  const res = await apiFetch(ROOM_ENDPOINTS.interiorRestore(roomId, imageId, interiorId), {
    method: 'POST',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || result.detail || `Failed to restore interior (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return result;
}

/**
 * PUT /api/v1/rooms/{room_id}
 * Updates room details (camelCase body: name, description, latestImageId, …).
 */
export async function updateRoom(roomId, data) {
  const res = await apiFetch(`${ROOM_ENDPOINTS.create}${roomId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data ?? {}),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || result.detail || `Failed to update room (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  console.log('PUT /rooms/{room_id} response:', result);
  return result;
}

/**
 * DELETE /api/v1/rooms/{room_id}
 * Deletes a room.
 */
export async function deleteRoom(roomId) {
  const res = await apiFetch(`${ROOM_ENDPOINTS.create}${roomId}`, {
    method: 'DELETE',
  });

  if (!res.ok) {
    let result = {};
    try {
      result = await res.json();
    } catch (_) {}
    const msg = result.message || result.error || result.detail || `Failed to delete room (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  console.log('DELETE /rooms/{room_id} success:', roomId);
}
